import BaseWrapper from "../../../components/ui/BaseWrapper";
import dayjs from "dayjs";

import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  deleteEngineerInvoiceWorkOrder,
  deleteInvoiceWorkOrder,
  downloadInvoice,
  downloadInvoiceProof,
  fetchAllEngineerWorkOrderByInvoice,
  fetchAllWorkOrderByInvoice,
  paidCustomerInvoice,
  paidEngineerInvoice,
  sendCustomerInvoice,
  verifyCustomerInvoice,
  verifyEngineerInvoice,
} from "../../../services/api/invoice";
import { ModuleType, BankAccountType, PaymentType, UserType } from "../../../utils/enum";
import BaseButton from "../../../components/ui/BaseButton";
import CountUp from "react-countup";
import SendInvoiceModal from "../components/SendInvoiceModal";
import { Fragment, useState } from "react";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { showToast } from "../../../store/toast/toastSlice";
import BaseDragDrop from "../../../components/ui/BaseDragDrop";
import { saveAs } from "file-saver";
import AddInvoiceNoteModal from "../components/AddInvoiceNoteModal";
import AddWorkOrderToInvoiceModal from "../components/AddWorkOrderToInvoiceModal";
import useAuth from "../../../hooks/useAuth";
import { displayFormattedTotalTimelog, formatTwoDecimals } from "../../../utils/helpers";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const EditInvoiceTable = ({ invoice, refetchInvoiceDetails }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getPermissionForModule, authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const { hasUpdateAccess: hasCustomerUpdateAccess } = getPermissionForModule(
    ModuleType.CUSTOMER_INVOICE,
  );
  const { hasUpdateAccess: hasEngineerUpdateAccess } = getPermissionForModule(
    ModuleType.ENGINEER_INVOICE,
  );
  const { hasWriteAccess: hasVerifyCustomerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_VERIFY_CUSTOMER_INVOICE,
  );

  const { hasWriteAccess: hasVerifyEngineerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_VERIFY_ENGINEER_INVOICE,
  );

  const { hasWriteAccess: hasSendCustomerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_SEND_CUSTOMER_INVOICE,
  );

  const { hasWriteAccess: hasSendEngineerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_SEND_ENGINEER_INVOICE,
  );

  const { hasWriteAccess: hasPaidCustomerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_PAID_CUSTOMER_INVOICE,
  );

  const { hasWriteAccess: hasPaidEngineerActionWriteAccess } = getPermissionForModule(
    ModuleType.ACTION_PAID_ENGINEER_INVOICE,
  );

  const [searchParams] = useSearchParams();
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [invoiceProof, setInvoiceProof] = useState(null);
  // Surfaced under the drop zone when Paid is confirmed with nothing attached.
  const [invoiceProofError, setInvoiceProofError] = useState("");
  const [isVerifyInvoiceConfirmModal, setIsVerifyInvoiceConfirmModal] = useState(false);
  const [isPaidInvoiceConfirmModal, setIsPaidInvoiceConfirmModal] = useState(false);
  const [isApproveInvoiceConfirmModal, setIsApproveInvoiceConfirmModal] = useState(false);

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isAddWorkOrderModalOpen, setIsAddWorkOrderModalOpen] = useState(false);

  const role = searchParams.get("role");

  // A paid invoice is settled, so its amount is frozen — the same rule the add
  // endpoint enforces. Customers never edit an invoice's contents.
  const canAddWorkOrders =
    !isCustomer &&
    invoice?.status !== "PAID" &&
    (role === UserType.CLIENT ? hasCustomerUpdateAccess : hasEngineerUpdateAccess);

  const {
    isPending: isDownloadInvoicePdfLoading,
    error: isDownloadInvoicePdfError,
    data: invoiceData,
    mutateAsync: downloadInvoicePdf,
  } = useMutation({
    mutationFn: downloadInvoice,
    onSuccess: (timeSheetBlob) => {
      const fileName = `INV-#${invoice.id}.pdf`;
      saveAs(timeSheetBlob, fileName);
    },
    onError: (error) => {
      console.error("Failed to download zip:", error);
    },
  });

  const {
    isPending: isDownloadInvoiceProofLoading,
    error: isDownloadInvoiceProofError,
    // data: invoiceData,
    mutateAsync: downloadInvoiceProofMutation,
  } = useMutation({
    mutationFn: downloadInvoiceProof,
    onSuccess: (invoiceProofBlob) => {
      const fileName = `INV-#${invoice.id}-Proof`;
      saveAs(invoiceProofBlob, fileName);
    },
    onError: (error) => {
      console.error("Failed to download zip:", error);
    },
  });

  const {
    isPending: isFetchAllWorkOrderByInvoiceApiLoading,
    data: invoiceWorkOrders,
    refetch: refetchAllWorkOrderByInvoice,
  } = useQuery({
    queryKey: ["invoiceWorkOrders", invoice?.id, role],
    queryFn:
      role === UserType.CLIENT
        ? () => fetchAllWorkOrderByInvoice(invoice?.id, role)
        : () => fetchAllEngineerWorkOrderByInvoice(invoice?.id, role),
    enabled: !!invoice?.id && !!role,
  });

  const {
    isPending: isUpdateInvoiceStatusLoading,
    error: updateInvoiceStatusError,
    data: updatedInvoice,
    mutateAsync: verifyCustomerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status }) => verifyCustomerInvoice(id, { status, file: invoiceProof }),

    enabled: !!invoice?.id,
  });

  const {
    isPending: isVerifyEngineerInvoiceApiLoading,
    error: isVerifyEngineerInvoiceApiError,
    mutateAsync: verifyEngineerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status }) => verifyEngineerInvoice(id, { status, file: invoiceProof }),

    enabled: !!invoice?.id,
  });

  const {
    isPending: isPaidCustomerInvoiceApiLoading,
    error: isPaidCustomerInvoiceApiError,
    mutateAsync: paidCustomerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status }) => paidCustomerInvoice(id, { status, file: invoiceProof }),
    enabled: !!invoice?.id,
  });

  const {
    isPending: isPaidEngineerInvoiceApiLoading,
    error: isPaidEngineerInvoiceApiError,
    mutateAsync: paidEngineerInvoiceMutation,
  } = useMutation({
    mutationFn: ({ id, status }) => paidEngineerInvoice(id, { status, file: invoiceProof }),
    enabled: !!invoice?.id,
  });

  const {
    isPending: isDeleteInvoiceWorkOrderApiLoading,
    error: isDeleteInvoiceWorkOrderApiError,
    data: deletedInvoiceWorkOrderData,
    mutateAsync: deleteInvoiceWorkOrderMutation,
  } = useMutation({
    mutationFn: role === UserType.CLIENT ? deleteInvoiceWorkOrder : deleteEngineerInvoiceWorkOrder,
    enabled: false,
  });

  // A customer approves rather than composing an email, so the confirm modal
  // sends the same request the full modal would, with the recipients the backend
  // already resolves (their own account address plus the client's branch).
  const { isPending: isApproveInvoiceApiLoading, mutateAsync: approveCustomerInvoiceMutation } =
    useMutation({
      mutationFn: ({ id, status, toEmail }) =>
        sendCustomerInvoice(id, { status, toEmail, skipEmail: false }),
      enabled: !!invoice?.id,
    });

  const handleApproveInvoice = async () => {
    try {
      await approveCustomerInvoiceMutation({
        id: Number(invoice?.id),
        status: "UNPAID",
        // Their account address, falling back to the client record's own email so
        // approving cannot hard-fail on "Invalid Email" when no account exists.
        toEmail: invoice?.client?.clientAccount?.email || invoice?.client?.email,
      });
      refetchInvoiceDetails();
      setIsApproveInvoiceConfirmModal(false);
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };

  const handleVerifyInvoiceStatus = async (status) => {
    try {
      if (role === UserType.CLIENT) {
        await verifyCustomerInvoiceMutation({
          id: Number(invoice?.id),
          status: status,
          file: invoiceProof || null,
        });
      } else {
        await verifyEngineerInvoiceMutation({
          id: Number(invoice?.id),
          status: status,
          file: invoiceProof || null,
        });
      }
      refetchInvoiceDetails();
      setIsVerifyInvoiceConfirmModal(false);
      setIsPaidInvoiceConfirmModal(false);
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };

  const handlePaidInvoiceStatus = async (status) => {
    // The proof is what the paid status is evidenced by, so it is required. The
    // API rejects a missing file too; catching it here keeps the modal open with
    // the message next to the field instead of spending a round trip on it.
    if (!invoiceProof) {
      setInvoiceProofError("Please attach invoice proof before marking this invoice as paid.");
      return;
    }

    try {
      if (role === UserType.CLIENT) {
        await paidCustomerInvoiceMutation({
          id: Number(invoice?.id),
          status: status,
          file: invoiceProof || null,
        });
      } else {
        await paidEngineerInvoiceMutation({
          id: Number(invoice?.id),
          status: status,
          file: invoiceProof || null,
        });
      }
      refetchInvoiceDetails();
      setIsVerifyInvoiceConfirmModal(false);
      setIsPaidInvoiceConfirmModal(false);
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(
        `/workOrder/payment/${params?.id}${role === UserType.CLIENT ? "?fromCustomerBoard=true" : ""}`,
      );
    }
    if (value === "DELETE") {
      handleDeleteInvoiceWorkOrder(params?.id);
    }
  };

  const handleDeleteInvoiceWorkOrder = async (workOrderId) => {
    try {
      await deleteInvoiceWorkOrderMutation({
        workOrderId,
        invoiceId: invoice.id,
      });
      await refetchInvoiceDetails();
      await refetchAllWorkOrderByInvoice();
      dispatch(
        showToast({
          type: "success",
          message: `Work Order removed from Invoice INV-#$${invoice.id} successfully`,
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: error.response.data.message,
        }),
      );
    }
  };

  const FilePreview = ({ file }) => {
    const type = file.type;

    if (type.startsWith("image/")) {
      return (
        <img
          className="h-10 w-10 rounded-sm object-cover"
          src={URL.createObjectURL(file)}
          alt="preview"
        />
      );
    }

    if (type === "application/pdf") {
      return <i class="fa-regular fa-file-pdf text-red-500 text-lg"></i>;
    }

    if (type.includes("word")) {
      return <i class="fa-regular fa-file-word text-blue-500 text-lg"></i>;
    }

    if (type.includes("excel") || type.includes("sheet") || type.includes("csv")) {
      return <i class="fa-regular fa-file-excel text-green-600 text-lg"></i>;
    }

    return <i class="fa-regular fa-file text-lg"></i>;
  };

  const hasAdditionalHours = invoiceWorkOrders?.data?.some(
    (wo) => Number(wo?.payment?.additionalHours) > 0,
  );
  const hasNonBusinessHours = invoiceWorkOrders?.data?.some(
    (wo) => Number(wo?.payment?.nonBusinessHours) > 0,
  );
  const hasMinimumHours = invoiceWorkOrders?.data?.some(
    (wo) => Number(wo?.payment?.minimumHours) > 0,
  );
  const columns = [
    {
      field: "orderStartDate",
      headerName: "Date",
      width: 100,
      renderCell: (params) => (
        <p className="cursor-default">{`${dayjs(params.value).format("DD-MMM-YYYY") || ""}`}</p>
      ),
    },
    {
      field: "incidentId",
      headerName: "Work Order Details",
      width: 200,
      renderCell: (params) => <p className="cursor-default">{params.value}</p>,
    },
    {
      field: "project",
      headerName: "Project",
      width: 150,
      renderCell: (params) => <p className="cursor-default">{params.value?.title || ""}</p>,
    },
    {
      field: "address",
      headerName: "Location",
      width: 300,
      renderCell: (params) => <p className="cursor-default">{params.value || ""}</p>,
    },
    {
      field: "purchaseOrders",
      headerName: "Purchase Order",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const { purchaseOrders, purchaseOrderNumber } = params.row;

        if (purchaseOrderNumber && (!purchaseOrders || purchaseOrders.length === 0)) {
          return <p className="cursor-default">{purchaseOrderNumber}</p>;
        }

        if (purchaseOrders?.length) {
          return (
            <div
              className="truncate w-full text-ellipsis whitespace-nowrap"
              title={purchaseOrders.map((po) => po.number).join(", ")}
            >
              {purchaseOrders.map((po, index) => (
                <span key={po.id}>
                  {po.attachment?.url ? (
                    <a
                      href={po.attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary cursor-pointer hover:underline"
                    >
                      {po.number}
                    </a>
                  ) : (
                    <span className="cursor-default">{po.number}</span>
                  )}
                  {index !== purchaseOrders.length - 1 && ", "}
                </span>
              ))}
            </div>
          );
        }

        return <span>-</span>;
      },
    },
    {
      field: "payment",
      headerName: "Compensation Rates",
      width: 320,
      renderCell: (params) => {
        const amount = params.value.amount
          ? `${formatTwoDecimals(params.value.amount)} ${params.value.currency}/${
              PaymentType[params.value.paymentType]
            }`
          : "";

        const additionalHourlyRate = params.value.additionalHourlyRate
          ? `, ${formatTwoDecimals(params.value.additionalHourlyRate)} ${params.value.currency}/Additional hour`
          : "";
        const nonBusinessHourRate = params.value.nonBusinessHourRate
          ? `& ${formatTwoDecimals(params.value.nonBusinessHourRate)} ${params.value.currency}/OOH`
          : "";
        return (
          <div>
            <p className="cursor-default h-[20px]">
              {amount + (hasAdditionalHours ? additionalHourlyRate : "") + nonBusinessHourRate}
            </p>
            {params.row?.Expense?.length > 0 && (
              <p className="cursor-default">
                Expenses:
                {params.row?.Expense?.map((expense) => (
                  <Fragment key={expense.id}>
                    <span> {expense.amount}</span>
                    <span> {params.value.currency}</span>/
                    {expense.expenseType === "OTHER" && expense.description ? (
                      <span>{expense.description}, </span>
                    ) : (
                      <span className="lowercase">{expense.expenseType}, </span>
                    )}
                  </Fragment>
                ))}
              </p>
            )}
          </div>
        );
      },
    },
    ...((invoice?.role === UserType.CLIENT || invoice?.role === UserType.FREELANCER) &&
    hasMinimumHours
      ? [
          {
            field: "minimumHours",
            headerName: "Minimum Hours",
            width: 200,
            renderCell: (params) => (
              <p className="cursor-default">
                {params.row.payment?.paymentType === "FULL_DAY"
                  ? PaymentType[params.row.payment?.paymentType]
                  : params.row.payment?.paymentType === "HALF_DAY"
                    ? PaymentType[params.row.payment?.paymentType]
                    : params.row.payment?.paymentType === "FIXED"
                      ? PaymentType[params.row.payment?.paymentType]
                      : displayFormattedTotalTimelog(params.row.payment?.minimumHours) || 0}
              </p>
            ),
          },
        ]
      : []),
    ...(invoice?.role === UserType.FREELANCER
      ? [
          {
            field: "hoursWorked",
            headerName: "Total Working Hours",
            width: 200,
            renderCell: (params) => (
              <p className="cursor-default">
                {displayFormattedTotalTimelog(params.row.payment?.hoursWorked) || 0}
              </p>
            ),
          },
        ]
      : []),
    ...(hasAdditionalHours
      ? [
          {
            field: "Additional Payable Hours",
            headerName: "Additional Hours",
            width: 200,
            renderCell: (params) => (
              <p className="cursor-default">
                {displayFormattedTotalTimelog(params.row.payment?.additionalHours) || 0}
              </p>
            ),
          },
        ]
      : []),
    ,
    ...(invoice?.role === UserType.CLIENT && hasNonBusinessHours
      ? [
          {
            field: "OOH Payable Hours",
            headerName: "Non Business Hours",
            width: 200,
            renderCell: (params) => (
              <p className="cursor-default">
                {(params.row.payment.nonBusinessHours || 0) +
                  (params.row.payment.nonBusinessHours > 1 ? " Hrs" : " Hr")}
              </p>
            ),
          },
        ]
      : []),
    {
      field: "payableAmount",
      headerName: "Total Amount",
      width: 320,
      renderCell: (params) => (
        <p className="cursor-default">{`${formatTwoDecimals(params.row.payment.grandTotal)} ${params.row.payment.currency}`}</p>
      ),
    },
    {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const menuOptions =
          invoice.role === UserType.CLIENT
            ? [
                ...(hasCustomerUpdateAccess && invoice.status !== "PAID"
                  ? [
                      { label: "Edit", value: "EDIT" },
                      { label: "Remove", value: "DELETE" },
                    ]
                  : []),
              ]
            : [
                ...(hasEngineerUpdateAccess && invoice.status !== "PAID"
                  ? [
                      { label: "Edit", value: "EDIT" },
                      { label: "Remove", value: "DELETE" },
                    ]
                  : []),
              ];
        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i class="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
    },
  ];

  const handleDownloadInvoicePdf = async () => {
    try {
      await downloadInvoicePdf(invoice.id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadInvoiceProof = async () => {
    try {
      await downloadInvoiceProofMutation(invoice.id);
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <BaseWrapper>
      {/* Also shown when the invoice has no work orders left, so removing the
          last one does not strand the invoice with no way to add another. */}
      {(invoiceWorkOrders?.data?.length > 0 || canAddWorkOrders) && (
        <div className="bg-white rounded-lg px-5 py-2 shadow-md mt-2 flex justify-between">
          <div>
            <BaseButton
              isLoading={isDownloadInvoicePdfLoading}
              color="primary"
              loaderSize="22px"
              variant="outlined"
              className="py-[4px] px-[34px] "
              onClick={handleDownloadInvoicePdf}
            >
              Download Invoice
            </BaseButton>
            {invoice?.digitalAssets?.length > 0 && invoice.status === "PAID" && (
              <BaseButton
                isLoading={isDownloadInvoiceProofLoading}
                color="primary"
                loaderSize="22px"
                variant="outlined"
                className="py-[4px] px-[34px] ml-4"
                onClick={handleDownloadInvoiceProof}
              >
                Download Proof
              </BaseButton>
            )}
            {!isCustomer && (
              <BaseButton
                color="primary"
                loaderSize="22px"
                variant="outlined"
                className="py-[4px] px-[20px] ml-4"
                onClick={() => {
                  setIsAddNoteModalOpen(true);
                }}
              >
                Add Note
              </BaseButton>
            )}
            {canAddWorkOrders && (
              <BaseButton
                color="primary"
                loaderSize="22px"
                variant="outlined"
                className="py-[4px] px-[20px] ml-4"
                onClick={() => setIsAddWorkOrderModalOpen(true)}
              >
                Add Work Order
              </BaseButton>
            )}
          </div>
          <div>
            {invoice?.status === "GENERATED" &&
              hasVerifyCustomerActionWriteAccess &&
              role === UserType.CLIENT && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px]"
                  onClick={() => setIsVerifyInvoiceConfirmModal(true)}
                >
                  Verify
                </BaseButton>
              )}
            {invoice?.status === "GENERATED" &&
              hasVerifyEngineerActionWriteAccess &&
              role === UserType.FREELANCER && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px]"
                  onClick={() => setIsVerifyInvoiceConfirmModal(true)}
                >
                  Verify
                </BaseButton>
              )}
            {invoice?.status === "VERIFIED" &&
              role === UserType.CLIENT &&
              hasSendCustomerActionWriteAccess && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px] ml-6"
                  onClick={() =>
                    isCustomer
                      ? setIsApproveInvoiceConfirmModal(true)
                      : setIsSendInvoiceModalOpen(true)
                  }
                >
                  {/* Sending is how a customer approves the invoice. */}
                  {isCustomer ? "Approve" : "Send"}
                </BaseButton>
              )}
            {invoice?.status === "VERIFIED" &&
              role === UserType.FREELANCER &&
              hasSendEngineerActionWriteAccess && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px] ml-6"
                  onClick={() => setIsSendInvoiceModalOpen(true)}
                >
                  Send
                </BaseButton>
              )}
            {(invoice?.status === "UNPAID" || invoice?.status === "OVERDUE") &&
              hasPaidCustomerActionWriteAccess &&
              role === UserType.CLIENT && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px] ml-6"
                  onClick={() => setIsPaidInvoiceConfirmModal(true)}
                >
                  PAID
                </BaseButton>
              )}

            {(invoice?.status === "UNPAID" || invoice?.status === "OVERDUE") &&
              hasPaidEngineerActionWriteAccess &&
              role === UserType.FREELANCER && (
                <BaseButton
                  color="primary"
                  className="py-[4px] px-[34px] ml-6"
                  onClick={() => setIsPaidInvoiceConfirmModal(true)}
                >
                  PAID
                </BaseButton>
              )}
          </div>
        </div>
      )}

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={isCustomer ? columns.filter((c) => c.headerName !== "Actions") : columns}
          data={invoiceWorkOrders?.data || []}
          isLoading={isFetchAllWorkOrderByInvoiceApiLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick
          checkboxSelection={false}
          hideFooter={false}
          footer={() => (
            <InvoiceFooter
              subTotal={formatTwoDecimals(invoice?.subTotal) || 0}
              taxAmount={formatTwoDecimals(invoice?.taxAmount)}
              currency={invoice?.currency || "N/A"}
              taxType={invoice?.taxType || "N/A"}
              taxPercentage={invoice?.taxPercentage || 0}
              grandTotal={formatTwoDecimals(invoice?.grandTotal) || 0}
              bankAccount={invoice?.bankAccount}
            />
          )}
        />
      </div>

      {invoice?.comment && (
        <p className="mt-4">
          <span className="font-normal text-base">Note: </span> {invoice?.comment}
        </p>
      )}
      {isSendInvoiceModalOpen && (
        <SendInvoiceModal
          billingToEmail={
            invoice.role === UserType.CLIENT
              ? invoice?.client?.clientAccount?.email
              : invoice.role === UserType.FREELANCER
                ? // The vendor's company address when it has one, so the invoice
                  // reaches the same party it bills from.
                  invoice.billingFrom?.email || invoice.engineer.email
                : null
          }
          role={role}
          open={isSendInvoiceModalOpen}
          close={setIsSendInvoiceModalOpen}
          invoiceId={invoice.id}
          refetchInvoiceDetails={refetchInvoiceDetails}
        />
      )}

      {isApproveInvoiceConfirmModal && (
        <BaseConfirmModal
          open={true}
          onClose={() => setIsApproveInvoiceConfirmModal(false)}
          onConfirm={handleApproveInvoice}
          confirmText="Approve"
          title="Approve this invoice?"
          description={
            <p className="w-[420px] pr-12">
              A copy will be emailed to you and to {invoice?.branch?.email || "the client"}. Once
              approved, this action cannot be undone.
            </p>
          }
          isLoading={isApproveInvoiceApiLoading}
        />
      )}

      {isVerifyInvoiceConfirmModal && (
        <BaseConfirmModal
          open={true}
          onClose={() => setIsVerifyInvoiceConfirmModal(false)}
          onConfirm={() => handleVerifyInvoiceStatus("VERIFIED")}
          confirmText="Verify"
          title="Have you reviewed the invoice carefully?"
          description={
            <p className="w-[420px] pr-12">
              Please confirm you have reviewed the invoice carefully. Once submitted, this action
              cannot be undone.
            </p>
          }
          isLoading={isUpdateInvoiceStatusLoading}
        />
      )}
      {isPaidInvoiceConfirmModal && (
        <BaseConfirmModal
          isLoading={isPaidCustomerInvoiceApiLoading || isPaidEngineerInvoiceApiLoading}
          open={true}
          onClose={() => {
            setInvoiceProofError("");
            setIsPaidInvoiceConfirmModal(false);
          }}
          onConfirm={() => handlePaidInvoiceStatus("PAID")}
          confirmText="Paid"
          title="Have you reviewed the invoice carefully?"
          description={
            <div>
              <p className="w-[420px] pr-12 mb-2">
                Please confirm you have reviewed the invoice carefully. Once paid, this action
                cannot be undone.
              </p>
              <BaseDragDrop
                onDrop={(file) => {
                  setInvoiceProof(file[0]);
                  setInvoiceProofError("");
                }}
                className="py-8"
              >
                <i class="fa-solid fa-upload text-xl text-primary"></i>
                <p className="text-primary mt-1"> Please attach invoice proof</p>
              </BaseDragDrop>
              {invoiceProofError && (
                <p className="text-red-600 text-sm mt-1">{invoiceProofError}</p>
              )}
              {invoiceProof && (
                <div className="flex items-center justify-between bg-light rounded-md py-1.5 px-2 cursor-default">
                  <div className="flex items-center">
                    <FilePreview file={invoiceProof} />

                    {/* <img
                      src={getFilePreview()}
                      alt="Profile Preview"
                      className="h-10 object-cover rounded-sm"
                    /> */}
                    <p className="ml-3">{invoiceProof?.name}</p>
                  </div>
                </div>
              )}
            </div>
          }
        />
      )}
      {isAddNoteModalOpen && (
        <AddInvoiceNoteModal
          open={isAddNoteModalOpen}
          close={setIsAddNoteModalOpen}
          invoice={invoice}
          refetchInvoiceDetails={refetchInvoiceDetails}
        />
      )}
      {isAddWorkOrderModalOpen && (
        <AddWorkOrderToInvoiceModal
          open={isAddWorkOrderModalOpen}
          close={setIsAddWorkOrderModalOpen}
          invoice={invoice}
          role={role}
          // Same pair the delete handler refetches — the totals live on the
          // invoice, the rows on the work order list.
          onAdded={async () => {
            await refetchInvoiceDetails();
            await refetchAllWorkOrderByInvoice();
          }}
        />
      )}
    </BaseWrapper>
  );
};

function InvoiceFooter({
  subTotal,
  currency,
  taxType,
  taxPercentage,
  taxAmount,
  grandTotal,
  bankAccount,
}) {
  return (
    <div className="flex justify-between mt-4">
      <div className="mt-6">
        <p className="text-primary text-base font-semibold mb-2">Payment Advice</p>
        {bankAccount?.accountHolderName && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Account Holder Name:</p>
            <p class="ml-2 font-light">{bankAccount?.accountHolderName}</p>
          </div>
        )}
        {bankAccount?.bankName && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Bank Name:</p>
            <p class="ml-2 font-light">{bankAccount?.bankName}</p>
          </div>
        )}
        {bankAccount?.bankAddress && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Bank Address:</p>
            <p class="ml-2 font-light">{bankAccount?.bankAddress}</p>
          </div>
        )}
        {bankAccount?.accountType && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Account Type</p>
            <p class="ml-2 font-light">{BankAccountType[bankAccount?.accountType]}</p>
          </div>
        )}
        {bankAccount?.currency && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Currency:</p>
            <p class="ml-2 font-light">{bankAccount?.currency}</p>
          </div>
        )}
        {bankAccount?.accountNumber && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Account Number</p>
            <p class="ml-2 font-light">{bankAccount?.accountNumber}</p>
          </div>
        )}
        {bankAccount?.swiftCode && (
          <div class="flex items-center mb-1">
            <p class="font-medium">BIC / Swift Code:</p>
            <p class="ml-2 font-light">{bankAccount?.swiftCode}</p>
          </div>
        )}
        {bankAccount?.routingNumber && (
          <div class="flex items-center mb-1">
            <p class="font-medium">ACH Routing:</p>
            <p class="ml-2 font-light">{bankAccount?.routingNumber}</p>
          </div>
        )}
        {bankAccount?.iban && (
          <div class="flex items-center mb-1">
            <p class="font-medium">IBAN:</p>
            <p class="ml-2 font-light">{bankAccount?.iban}</p>
          </div>
        )}
        {bankAccount?.sortCode && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Sort Code:</p>
            <p class="ml-2 font-light">{bankAccount?.sortCode}</p>
          </div>
        )}
        {bankAccount?.institutionNumber && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Institution Number:</p>
            <p class="ml-2 font-light">{bankAccount?.institutionNumber}</p>
          </div>
        )}
        {bankAccount?.transitNumber && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Transit Number:</p>
            <p class="ml-2 font-light">{bankAccount?.transitNumber}</p>
          </div>
        )}
        {bankAccount?.bankCode && (
          <div class="flex items-center mb-1">
            <p class="font-medium">Bank Code:</p>
            <p class="ml-2 font-light">{bankAccount?.bankCode}</p>
          </div>
        )}
        {bankAccount?.bsb && (
          <div class="flex items-center mb-1">
            <p class="font-medium">BSB:</p>
            <p class="ml-2 font-light">{bankAccount?.bsb}</p>
          </div>
        )}
      </div>
      <div>
        <div className="flex mb-2">
          <p className="min-w-44 text-sm text-dark font-medium">Subtotal</p>
          <p className="text-sm text-dark font-medium">
            {`${currency} `}
            <CountUp
              start={0}
              end={subTotal || 0}
              duration={0}
              separator=","
              decimals={2}
              decimal="."
            />
          </p>
        </div>
        {taxType !== "N/A" || taxPercentage !== 0 ? (
          <div className="flex mb-2">
            <p className="min-w-44 text-sm text-dark font-medium">{`${taxType} (${taxPercentage}%)`}</p>
            <p className="text-sm text-dark font-medium">
              {`${currency} `}
              <CountUp
                start={0}
                end={taxAmount || 0}
                duration={0}
                separator=","
                decimals={2}
                decimal="."
              />
            </p>
          </div>
        ) : (
          <div className="flex mb-2">
            <p className="min-w-44 text-sm text-dark font-medium">{`VAT/GST`}</p>
            <p className="text-sm text-dark font-medium">N/A</p>
          </div>
        )}
        <div className="flex mb-2 border-y border-zinc-400 py-2">
          <p className="min-w-44 text-sm text-primary font-bold">{`Total`}</p>

          <p className="text-sm text-primary font-bold min-w-24 ">
            {`${currency} `}

            <CountUp
              start={0}
              end={grandTotal || 0}
              duration={2}
              separator=","
              decimals={2}
              decimal="."
            />
          </p>
        </div>
      </div>
    </div>
  );
}

export default EditInvoiceTable;
